import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Hls from 'hls.js';
import { IMAGE_CONSTANTS } from '../../constants/ImageConstans';
import { duckBgm, restoreBgm, resumeBgm } from '../../utils/bgm';
import {
  RAP_FIRE_QUESTION_COUNT_TEXT_STYLE,
  RAP_FIRE_QUESTION_COUNT_WRAP_STYLE,
} from '../../constants/housieLiveStyles';

const TIMER_SECONDS = 10;
/** Cloudflare Stream clip — audio-only rendition of the timer sound. */
const TIMER_SOUND_URL =
  'https://customer-neooj6eu5wlxrhme.cloudflarestream.com/c81d3af90355270765035040496769d8/manifest/stream_t1661831d545e922abbe12b50350b9f72_r1868245480.m3u8';
/** Figma 18011:22066 — outer ellipse */
const RING_SIZE = 100;
/** Figma 18011:22067 — inner disc */
const INNER_SIZE = 92.308;
const ACTIVE_COLOR = '#FF00F2';
const EMPTY_COLOR = '#FFFFFF';

/**
 * Rap Fire / Housie answer timer — counts down.
 * Figma 18011:22064 / 22066 / 22068 — 100px ring, centered digits.
 */
const RapFireQuestionCount = ({
  questionNumber = 1,
  durationSeconds = TIMER_SECONDS,
  onComplete,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(durationSeconds);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const runningRef = useRef(true);
  onCompleteRef.current = onComplete;
  const isRunning = secondsLeft > 1;
  runningRef.current = isRunning;

  useEffect(() => {
    completedRef.current = false;
    setSecondsLeft(durationSeconds);
  }, [questionNumber, durationSeconds]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const attemptPlay = () => {
      if (runningRef.current) {
        duckBgm();
        audio.play().catch(() => {});
      }
      resumeBgm();
    };
    const onPlaying = () => {
      window.removeEventListener('pointerdown', attemptPlay, true);
      window.removeEventListener('keydown', attemptPlay, true);
    };
    const onReady = () => {
      if (runningRef.current) attemptPlay();
    };
    const onEnded = () => {
      if (!runningRef.current) return;
      hlsRef.current?.stopLoad();
      audio.currentTime = 0;
      hlsRef.current?.startLoad(0);
      attemptPlay();
    };

    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('canplay', onReady);
    audio.addEventListener('ended', onEnded);
    window.addEventListener('pointerdown', attemptPlay, true);
    window.addEventListener('keydown', attemptPlay, true);

    if (audio.canPlayType('application/vnd.apple.mpegurl')) {
      audio.src = TIMER_SOUND_URL;
      audio.load();
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
      });
      hlsRef.current = hls;
      hls.loadSource(TIMER_SOUND_URL);
      hls.attachMedia(audio);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (runningRef.current) attemptPlay();
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data?.fatal) {
          hls.destroy();
          hlsRef.current = null;
        }
      });
    }

    return () => {
      restoreBgm();
      resumeBgm();
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      hlsRef.current?.destroy();
      hlsRef.current = null;
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('canplay', onReady);
      audio.removeEventListener('ended', onEnded);
      window.removeEventListener('pointerdown', attemptPlay, true);
      window.removeEventListener('keydown', attemptPlay, true);
    };
    // Attach once per question so the clip restarts with the counter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionNumber, durationSeconds]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isRunning) {
      duckBgm();
      audio.play().catch(() => {});
      resumeBgm();
    } else {
      audio.pause();
      restoreBgm();
      resumeBgm();
    }
  }, [isRunning]);

  useEffect(() => {
    if (secondsLeft <= 1) return undefined;

    const id = setTimeout(() => {
      setSecondsLeft((prev) => Math.max(1, prev - 1));
    }, 1000);

    return () => clearTimeout(id);
  }, [secondsLeft]);

  useEffect(() => {
    if (secondsLeft === 1 && !completedRef.current) {
      completedRef.current = true;
      if (typeof onCompleteRef.current === 'function') {
        onCompleteRef.current();
      }
    }
  }, [secondsLeft]);

  const progress = Math.max(0, Math.min(1, secondsLeft / durationSeconds));
  const progressDeg = progress * 360;
  const label = String(secondsLeft).padStart(2, '0');
  const innerBg = IMAGE_CONSTANTS.V2_LIVE_COUNTER_INNER;

  return (
    <div style={RAP_FIRE_QUESTION_COUNT_WRAP_STYLE} aria-label={`Timer ${label}`}>
      <audio
        ref={audioRef}
        preload="auto"
        aria-hidden="true"
        style={{ display: 'none' }}
      />
      <div
        style={{
          width: RING_SIZE,
          height: RING_SIZE,
          borderRadius: '50%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          /* Remaining = pink, empty (elapsed) = white — Figma 18011:22066 */
          background: `conic-gradient(${ACTIVE_COLOR} ${progressDeg}deg, ${EMPTY_COLOR} 0deg)`,
          boxShadow:
            '0px 16px 4px rgba(0, 72, 103, 0.16), 0px 0px 12px rgba(255, 0, 242, 0.35)',
          transition: 'background 0.35s linear',
        }}
      >
        <div
          style={{
            width: INNER_SIZE,
            height: INNER_SIZE,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#5c0018',
            backgroundImage: innerBg ? `url(${innerBg})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            flexShrink: 0,
          }}
        >
          <span style={RAP_FIRE_QUESTION_COUNT_TEXT_STYLE}>{label}</span>
        </div>
      </div>
    </div>
  );
};

RapFireQuestionCount.propTypes = {
  questionNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  durationSeconds: PropTypes.number,
  onComplete: PropTypes.func,
};

export default RapFireQuestionCount;
